import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", email: "", phone: "", address: "" };

export default function CheckoutModal({ open, onOpenChange }) {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "कृपया अपना नाम भरें";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "कृपया मान्य ईमेल भरें";
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 10) return "कृपया 10 अंकों का मान्य फ़ोन नंबर भरें";
    if (form.address.trim().length < 10) return "कृपया पूरा पता भरें";
    return null;
  };

  const handlePay = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders/create`, form);
      const paymentSessionId = data.payment_session_id;
      if (!paymentSessionId || !window.Cashfree) {
        throw new Error("payment-init-failed");
      }
      const cashfree = window.Cashfree({ mode: "production" });
      await cashfree.checkout({
        paymentSessionId,
        redirectTarget: "_self",
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "भुगतान आरंभ करने में समस्या आई। कृपया पुनः प्रयास करें।");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !loading && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md bg-white border-brandgreen/20" data-testid="checkout-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brandgreen-dark">
            अपनी प्रति सुरक्षित करें
          </DialogTitle>
          <DialogDescription className="font-sans text-brandgreen/70">
            शुद्ध दिनचर्या – सुख और स्वास्थ्य · केवल ₹150
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="name" className="font-sans text-brandgreen-dark">पूरा नाम</Label>
            <Input id="name" data-testid="checkout-name" value={form.name} onChange={update("name")}
              placeholder="आपका नाम" disabled={loading}
              className="focus-visible:ring-brandgold" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="font-sans text-brandgreen-dark">ईमेल</Label>
            <Input id="email" type="email" data-testid="checkout-email" value={form.email} onChange={update("email")}
              placeholder="you@example.com" disabled={loading}
              className="focus-visible:ring-brandgold" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone" className="font-sans text-brandgreen-dark">फ़ोन नंबर</Label>
            <Input id="phone" type="tel" data-testid="checkout-phone" value={form.phone} onChange={update("phone")}
              placeholder="10 अंकों का मोबाइल नंबर" disabled={loading}
              className="focus-visible:ring-brandgold" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="address" className="font-sans text-brandgreen-dark">डिलीवरी का पता</Label>
            <Textarea id="address" data-testid="checkout-address" value={form.address} onChange={update("address")}
              placeholder="घर/मकान नं., गली, शहर, राज्य, पिन कोड" rows={3} disabled={loading}
              className="focus-visible:ring-brandgold resize-none" />
          </div>
        </div>

        <Button
          onClick={handlePay}
          disabled={loading}
          data-testid="checkout-pay-button"
          className="w-full rounded-full bg-brandgold hover:bg-brandgold-hover text-brandgreen-dark font-sans font-bold text-lg py-6 hover:scale-[1.02] transition-transform"
        >
          {loading ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> भुगतान पर ले जा रहे हैं...</>
          ) : (
            <>₹150 का भुगतान करें</>
          )}
        </Button>

        <p className="font-sans text-xs text-brandgreen/60 text-center flex items-center justify-center gap-1.5">
          <Lock className="w-3.5 h-3.5" /> Cashfree द्वारा 100% सुरक्षित भुगतान
        </p>
      </DialogContent>
    </Dialog>
  );
}
